import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Row,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { RankingDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { LabelsTags } from '@/components/common/LabelsField';
import { formatDateTime } from '@/utils/format';
import { PublicationRankingsCard } from '@/components/user_catalog/rankings/PublicationRankingsCard';
import { RankingPreviewPanel } from '@/components/user_catalog/rankings/RankingPreviewPanel';

interface RankingGeneralTabProps {
  record: RankingDetailOut;
}

export const RankingGeneralTab = ({ record }: RankingGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  const items: DescriptionsProps['items'] = [
    {
      key: 'slug',
      label: t('user_catalog.rankings.fields.slug'),
      children: orDash(
        record.slug && <Typography.Text code>{record.slug}</Typography.Text>
      ),
    },
    {
      key: 'labels',
      label: t('base.labels._field'),
      children: <LabelsTags labels={record.labels} />,
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.basic_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2, md: 3 }}
              items={items}
            />
          </Card>
          <PublicationRankingsCard
            publicationRankings={record.publication_rankings ?? []}
            movementSnapshotAt={record.movement_snapshot_at}
          />
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <RankingPreviewPanel record={record} />
      </Col>
    </Row>
  );
};
